import React, { useState, useRef, useEffect } from "react";
import dayjs from "dayjs";

const GridPairDatePicker = ({
  onFromDateChange,
  onToDateChange,
  fromDate,
  toDate,
  onClose,
  onClick,
}) => {
  const [isFromOpen, setIsFromOpen] = useState(false);
  const [isToOpen, setIsToOpen] = useState(false);
  const [isHoveredFrom, setIsHoveredFrom] = useState(false);
  const [isHoveredTo, setIsHoveredTo] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(dayjs());
  const [hoveredDate, setHoveredDate] = useState(null);

  const weekDays = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];
  const fromRef = useRef(null);
  const toRef = useRef(null);

  const generateCalendarDays = (month) => {
    const start = month.startOf("month").startOf("week");
    const end = month.endOf("month").endOf("week");
    const days = [];
    let day = start;

    while (day.isBefore(end)) {
      days.push(day);
      day = day.add(1, "day");
    }

    return days;
  };

  const isDateInRange = (date) => {
    if (!fromDate || !toDate) return false;
    const from = dayjs(fromDate);
    const to = dayjs(toDate);
    return date.isAfter(from) && date.isBefore(to);
  };

  const isDateDisabled = (date, type) => {
    const today = dayjs().startOf("day");
    if (date.isAfter(today)) return true;

    if (type === "to" && fromDate) {
      return date.isBefore(dayjs(fromDate));
    }

    return false;
  };

  useEffect(() => {
    if (fromDate && toDate) {
      setIsFromOpen(false);
      setIsToOpen(false);
      onClick();
    }
  }, [fromDate, toDate]);

  const renderCalendarGrid = (type) => {
    const days = generateCalendarDays(currentMonth);

    const handleDateClick = (date) => {
      if (isDateDisabled(date, type)) return;
      const formattedDate = date.format("YYYY-MM-DD");
      if (type === "from") {
        onFromDateChange(formattedDate);
        setIsFromOpen(false);
        if (!toDate) {
          setIsToOpen(true);
        }
      } else {
        onToDateChange(formattedDate);
        setIsToOpen(false);
      }
    };

    return (
      <div style={styles.calendar}>
        <div style={styles.calendarHeader}>
          <button
            style={styles.navigationButton}
            onClick={() => setCurrentMonth((curr) => curr.subtract(1, "month"))}
          >
            ←
          </button>
          <span style={styles.monthYearText}>
            {currentMonth.format("MMMM YYYY")}
          </span>
          <button
            style={styles.navigationButton}
            onClick={() => setCurrentMonth((curr) => curr.add(1, "month"))}
          >
            →
          </button>
        </div>

        <div style={styles.weekDaysGrid}>
          {weekDays.map((day) => (
            <div key={day} style={styles.weekDay}>
              {day}
            </div>
          ))}
        </div>

        <div style={styles.daysGrid}>
          {days.map((day, index) => {
            const isSelected =
              type === "from"
                ? dayjs(fromDate).isSame(day, "day")
                : dayjs(toDate).isSame(day, "day");
            const isToday = day.isSame(dayjs(), "day");
            const isDisabled = isDateDisabled(day, type);
            const isInRange = isDateInRange(day);
            const isHovered = hoveredDate?.isSame(day, "day");

            return (
              <div
                key={index}
                style={{
                  ...styles.dayCell,
                  ...(isHovered && !isDisabled ? styles.dayCellHover : {}),
                  ...(isSelected ? styles.dayCellSelected : {}),
                  ...(isInRange ? styles.dayCellInRange : {}),
                  ...(isDisabled ? styles.dayCellDisabled : {}),
                  ...(isToday ? styles.dayCellToday : {}),
                }}
                onClick={() => handleDateClick(day)}
                onMouseEnter={() => !isDisabled && setHoveredDate(day)}
                onMouseLeave={() => setHoveredDate(null)}
              >
                {day.format("D")}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (fromRef.current && !fromRef.current.contains(event.target)) {
        setIsFromOpen(false);
      }
      if (toRef.current && !toRef.current.contains(event.target)) {
        setIsToOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getCalendarPosition = (type) => {
    if (type === "to") {
      return {
        right: 0,
        left: "auto",
      };
    }
    return {
      left: 0,
      right: "auto",
    };
  };

  const renderDropdown = (
    type,
    value,
    isOpen,
    setIsOpen,
    isHovered,
    setIsHovered,
    ref
  ) => (
    <div ref={ref} style={styles.dropdownContainer}>
      <div
        style={{
          ...styles.dropdownHeader,
          ...(isHovered ? styles.dropdownHeaderHover : {}),
          ...(type === "to" && !fromDate ? styles.dropdownDisabled : {}),
        }}
        onClick={() => (fromDate || type === "from") && setIsOpen(!isOpen)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div style={styles.dropdownContent}>
          <span style={styles.dropdownTitle}>
            {type === "from" ? "From" : "To"}
          </span>
          <span style={styles.dropdownValue}>
            {value ? dayjs(value).format("DD/MM/YY") : "Select date"}
          </span>
        </div>
        <div style={styles.dropdownActions}>
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
          <div
            style={{
              ...styles.chevronIcon,
              ...(isOpen ? styles.chevronIconRotate : {}),
            }}
          >
            <svg
              width="12"
              height="12"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M6 9l6 6 6-6" />
            </svg>
          </div>
        </div>
      </div>
      {isOpen && (
        <div
          style={{
            ...styles.calendarWrapper,
            ...(type === "to" ? styles.calendarWrapperRight : {}),
          }}
        >
          {renderCalendarGrid(type)}
        </div>
      )}
    </div>
  );

  const styles = {
    container: {
      display: "flex",
      alignItems: "center",
      gap: "8px",
      position: "relative",
    },
    dropdownContainer: {
      position: "relative",
      width: "160px",
    },
    calendarWrapper: {
      position: "absolute",
      top: "100%",
      left: 0,
      zIndex: 1000,
    },
    calendarWrapperRight: {
      left: "auto",
      right: 0,
    },
    dropdownHeader: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: "8px 12px",
      backgroundColor: "white",
      borderRadius: "6px",
      cursor: "pointer",
      transition: "border-color 0.2s ease",
      border: "1px solid #ddd",
    },
    dropdownHeaderHover: {
      borderColor: "#999",
    },
    dropdownDisabled: {
      opacity: 0.5,
      cursor: "not-allowed",
    },
    dropdownContent: {
      display: "flex",
      flexDirection: "column",
      minWidth: 0,
      flex: 1,
    },
    dropdownTitle: {
      fontSize: "12px",
      fontWeight: 600,
      color: "#666",
    },
    dropdownValue: {
      fontSize: "14px",
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
    },
    dropdownActions: {
      display: "flex",
      alignItems: "center",
      gap: "4px",
      color: "#666",
    },
    chevronIcon: {
      transition: "transform 0.2s ease",
      display: "flex",
    },
    chevronIconRotate: {
      transform: "rotate(180deg)",
    },
    calendar: {
      backgroundColor: "white",
      border: "1px solid #ddd",
      borderRadius: "6px",
      boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
      padding: "16px",
      width: "280px",
    },
    calendarHeader: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      marginBottom: "16px",
    },
    monthYearText: {
      fontSize: "14px",
      fontWeight: 600,
      color: "#333",
    },
    navigationButton: {
      background: "none",
      border: "none",
      padding: "4px 8px",
      cursor: "pointer",
      color: "#666",
    },
    weekDaysGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      gap: "4px",
      marginBottom: "8px",
    },
    weekDay: {
      textAlign: "center",
      fontSize: "12px",
      fontWeight: "600",
      color: "#666",
      padding: "4px",
    },
    daysGrid: {
      display: "grid",
      gridTemplateColumns: "repeat(7, 1fr)",
      gap: "4px",
    },
    dayCell: {
      width: "32px",
      height: "32px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      cursor: "pointer",
      borderRadius: "4px",
      fontSize: "14px",
      transition: "all 0.2s ease",
    },
    dayCellHover: {
      backgroundColor: "#f0f0f0",
    },
    dayCellSelected: {
      backgroundColor: "#e6f4ff",
      color: "#1890ff",
      fontWeight: "600",
    },
    dayCellInRange: {
      backgroundColor: "#f0f7ff",
      color: "#1890ff",
    },
    dayCellDisabled: {
      color: "#ccc",
      cursor: "not-allowed",
    },
    dayCellToday: {
      fontWeight: "600",
      color: "#1890ff",
    },
    closeButton: {
      width: "32px",
      height: "32px",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      border: "none",
      borderRadius: "50%",
      cursor: "pointer",
      transition: "all 0.2s ease",
      backgroundColor: "rgba(234, 67, 53, 0.1)",
      color: "#EA4335",
      marginLeft: "8px",
    },
    closeButtonHover: {
      backgroundColor: "rgba(234, 67, 53, 0.2)",
    },
  };

  return (
    <div style={styles.container}>
      {renderDropdown(
        "from",
        fromDate,
        isFromOpen,
        setIsFromOpen,
        isHoveredFrom,
        setIsHoveredFrom,
        fromRef
      )}
      {renderDropdown(
        "to",
        toDate,
        isToOpen,
        setIsToOpen,
        isHoveredTo,
        setIsHoveredTo,
        toRef
      )}
      {fromDate && toDate && (
        <button
          onClick={onClose}
          style={{
            ...styles.closeButton,
            ...(isHoveredTo ? styles.closeButtonHover : {}),
          }}
          onMouseEnter={() => setIsHoveredTo(true)}
          onMouseLeave={() => setIsHoveredTo(false)}
        >
          <svg
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      )}
    </div>
  );
};

export default GridPairDatePicker;
